import React, { useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useIsMobile } from "hooks/useIsMobile";

import { datePickerPopperProps } from "components/admin/datePickerPopper";
import { useClickOutside } from "hooks/useClickOutside";

interface FeedbackFilterProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: { fromDate?: string; toDate?: string }) => void;
  onClear: () => void;
}

const FeedbackFilter: React.FC<FeedbackFilterProps> = ({
  isOpen,
  onClose,
  onApply,
  onClear,
}) => {
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const isMobile = useIsMobile();
  const today = new Date();
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Detect clicks outside the filter panel (only while open). The date
  // pickers render into a body-level portal (see datePickerPopperProps), so
  // clicks inside that portal must not count as "outside".
  useClickOutside(panelRef, onClose, isOpen, "mousedown", "#admin-datepicker-portal");

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({
      fromDate: fromDate ? fromDate.toISOString().split("T")[0] : undefined,
      toDate: toDate ? toDate.toISOString().split("T")[0] : undefined,
    });
    onClose();
  };

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    onClear();
    onClose();
  };

  return (
    <>
      {/* Backdrop — mobile only; the panel is centred on screen there because
          anchoring it to the button pushes it off the left edge. */}
      <div
        className="fixed inset-0 z-20 bg-black bg-opacity-40 sm:hidden"
        onClick={onClose}
      />
    <div
      ref={panelRef}
      className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-30 w-auto max-w-[calc(100vw-2rem)] sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:translate-y-0 sm:mt-2 sm:w-80 sm:max-w-none bg-white border border-gray-200 rounded-lg shadow-lg sm:z-50"
    >
      <div className="flex justify-end items-center p-3 border-b">
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 focus:outline-none"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <div className="p-4">
        <p className="text-sm">Feedback Date</p>
        <div className="flex flex-wrap sm:flex-nowrap gap-4 mt-3">
          <div className="flex-1 min-w-0">
            <label className="text-xs text-gray-500">From Date</label>
            <DatePicker
              selected={fromDate}
              onChange={(date: Date | null) => setFromDate(date)}
              dateFormat="dd/MM/yyyy"
              placeholderText="Select from date"
              className="p-3 rounded-lg bg-white text-xs w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royalblue-100"
              maxDate={toDate || today} // No future dates, and not after toDate
              {...datePickerPopperProps}
              popperPlacement="bottom-start"
            />
          </div>
          <div className="flex-1 min-w-0">
            <label className="text-xs text-gray-500">To Date</label>
            <DatePicker
              selected={toDate}
              onChange={(date: Date | null) => setToDate(date)}
              dateFormat="dd/MM/yyyy"
              placeholderText="Select to date"
              className="p-3 rounded-lg bg-white text-xs w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royalblue-100"
              minDate={fromDate || undefined} // Prevent selecting before fromDate
              maxDate={today} // No future dates
              {...datePickerPopperProps}
              popperPlacement="bottom-end"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-between p-3">
        <button
          className="px-3 py-2 bg-blue-500 bg-opacity-25 rounded-md"
          onClick={onClose}
        >
          Cancel
        </button>
        <div className="flex gap-4">
          <button
            className="px-3 py-2 border border-[#FFD700] rounded-md"
            onClick={handleClear}
          >
            Clear All
          </button>
          <button
            className="px-3 py-2 bg-[#FFD700] rounded-md text-white"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default FeedbackFilter;
